/* eslint-disable valid-jsdoc */
import { BotServiceConfig } from '@bingads-webui/ads-copilot-shared-types';

import {
  type TStructuredLogResponder,
  type IStructuredLogBase,
  type IHandleStructuredLogEventParams,
  type IStructuredLogConfig,
} from '../types/structured-log-types';
import { checkifOriginIsWhitelisted } from './check-whitelist-origin';
import { maskPII } from './mask-pii-helpers';

const NONE_USER_ACTIVITY_TYPES = [
  'Message',
  'Unknown',
  'LiveAgentAssigned',
  'LiveAgentAccepted',
  'LiveAgentConversationEnded',
  'LiveAgentPostChatSurveyDisplayed',
  'LiveAgentMessage',
  'LiveAgentQueueDisplayed',
  'ConversationResumedWithCopilot',
  'TestYourTagTool',
];

const getResponder = <T extends IStructuredLogBase>(
  botServiceConfig: BotServiceConfig,
  isCustomStream: boolean,
  eventDetail: T
): TStructuredLogResponder => {
  if (!NONE_USER_ACTIVITY_TYPES.includes(eventDetail.activityType)) {
    return 'User';
  }

  if (eventDetail.activityUsername === botServiceConfig.username) {
    return isCustomStream ? 'UserWithAgent' : 'User';
  }

  return isCustomStream ? 'Agent' : 'Copilot';
};

const createFinalStructuredLog = <T extends IStructuredLogBase>(
  params: Omit<IHandleStructuredLogEventParams<T>, 'logger' | 'structuredLogValidator'>
): IStructuredLogConfig & T => {
  const {
    event, botServiceConfig, isCustomStream, locale, conversationId,
  } = params;

  const { detail } = event as CustomEvent<T>;

  const responder = getResponder<T>(botServiceConfig, isCustomStream, detail);

  const sanitizedDetail = { ...detail };

  // Mask PII in detail
  if (detail.activityUsername && detail.activityUsername.includes('@')) {
    sanitizedDetail.activityUsername = detail.activityUsername.replace(/^[^@]+/, '****');
  }

  if (detail.activityText) {
    sanitizedDetail.activityText = maskPII(detail.activityText);
  }

  return {
    uid: botServiceConfig.userid,
    cid: botServiceConfig.customerId as string,
    aid: botServiceConfig.accountId as string,
    product: botServiceConfig.productName,
    locale,
    responder,
    timestamp: new Date().toUTCString(),
    ...sanitizedDetail,
    conversationId: sanitizedDetail.conversationId || conversationId,
  };
};

/**
 * `handleStructuredLogEvent` handles the structured log event
 * @param params - parameters to handle the structured log event
 * @param params.event - event to handle
 * @param params.botServiceConfig - bot service config to handle the structured log event
 * @param params.isCustomStream - whether the event is from a custom stream
 * @param params.locale - locale to handle the structured log event
 * @param params.logger - logger to log the structured log data or errors when `structuredLog` event is dispatched
 * @param params.structuredLogValidator - structured log validator to validate the structured log data
 */
export const handleStructuredLogEvent = <T extends IStructuredLogBase>(params: IHandleStructuredLogEventParams<T>) => {
  const {
    logger, structuredLogValidator, ...restParams
  } = params;

  if (!checkifOriginIsWhitelisted()) return;

  const finalStructuredLog = createFinalStructuredLog<T>(restParams);

  if (!structuredLogValidator) {
    logger.log(finalStructuredLog);
    return;
  }

  const { isValid, validValues, errorMessages } = structuredLogValidator(finalStructuredLog);
  if (isValid) {
    logger.log(validValues);
  } else {
    logger.error(validValues, errorMessages);
  }
};
